// hooks/useSellerProducts.ts
import { useState, useEffect } from "react";
import API from "../API/axios";

export interface Product {
  id: string;
  sellerId: string;
  categoryId: string;
  name: string;
  description: string;
  status: "PENDING_APPROVAL" | "APPROVED" | "REJECTED";
  createdAt: string;
  updatedAt: string;
  images: any[];
  category: any;
  variants: any[];
}

export const useSellerProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await API.get("/seller/my-products");

      // Ensure products is always an array
      let productsData = response.data;
      if (!Array.isArray(productsData)) {
        // If response is an object with data property, extract it
        if (productsData.data && Array.isArray(productsData.data)) {
          productsData = productsData.data;
        } else if (
          productsData.products &&
          Array.isArray(productsData.products)
        ) {
          productsData = productsData.products;
        } else {
          // If it's neither, try to convert or set empty array
          console.warn("Products data is not an array:", productsData);
          productsData = [];
        }
      }

      setProducts(productsData);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching products:", err);
      setError(err.response?.data?.message || "Failed to fetch products");
      setProducts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId: string) => {
    try {
      await API.delete(`/seller/delete-product/${productId}`);
      setProducts(products.filter((p) => p.id !== productId));
      return true;
    } catch (err: any) {
      console.error("Error deleting product:", err);
      setError(err.response?.data?.message || "Failed to delete product");
      return false;
    }
  };

  const updateStock = async (
    productId: string,
    variantId: string,
    stock: number,
  ) => {
    try {
      const response = await API.patch(
        `/seller/update-inventory/${productId}`,
        {
          variantId,
          stock,
        },
      );

      // Update local state
      setProducts(
        products.map((product) => {
          if (product.id === productId) {
            return {
              ...product,
              variants: product.variants.map((variant) => {
                if (variant.id === variantId) {
                  return {
                    ...variant,
                    inventory: {
                      ...variant.inventory,
                      stock: response.data.inventory?.stock || stock,
                      isOutOfStock: stock === 0,
                    },
                  };
                }
                return variant;
              }),
            };
          }
          return product;
        }),
      );

      return true;
    } catch (err: any) {
      console.error("Error updating stock:", err);
      setError(err.response?.data?.message || "Failed to update stock");
      return false;
    }
  };

  const updateProduct = async (productId: string, formData: FormData) => {
    try {
      console.log("Updating product with ID:", productId);
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      const response = await API.put(
        `/seller/update-product/${productId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      console.log("Update response:", response.data);

      // Update local state with the updated product
      setProducts(
        products.map((p) => (p.id === productId ? response.data : p)),
      );

      return response.data;
    } catch (err: any) {
      console.error("Update error:", err.response?.data || err);
      setError(err.response?.data?.message || "Failed to update product");
      throw err;
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return {
    products,
    loading,
    error,
    fetchProducts,
    deleteProduct,
    updateStock,
    updateProduct,
  };
};
